/* Toolbar — Phase B4 layout primitive.
 *
 * Horizontal control row that sits within a Page (typically just under
 * the PageHeader). Holds page-scoped controls — segmented toggles,
 * scenario pickers, timeframe selectors, refresh buttons — without
 * the inline-style boilerplate that's been duplicated across views.
 *
 * Two slot variants:
 *   - left  /  right     — opposed clusters, space-between
 *   - children-only       — single cluster, wrapped row (multi-line ok)
 *
 * Designed for ForecastItaly's ROW 2 (value-mode + scenarios + timeframe)
 * but generic — any view that wants a controls row can use it.
 *
 * Spacing: --space-3 gap between adjacent children inside each cluster,
 * --space-4 between left and right clusters when both are present. Wraps
 * gracefully on narrow viewports.
 *
 * Visual: subtle bottom border by default so the toolbar reads as
 * separated from the content below. Passing `borderless` removes it
 * (useful when the toolbar sits flush against a Card).
 */
import React from "react";

interface ToolbarProps {
  left?: React.ReactNode;
  right?: React.ReactNode;
  children?: React.ReactNode;
  borderless?: boolean;
  className?: string;
}

export default function Toolbar({
  left,
  right,
  children,
  borderless = false,
  className = "",
}: ToolbarProps) {
  const cls = `v2-toolbar${borderless ? " v2-toolbar--borderless" : ""} ${className}`.trim();

  // Two-cluster layout: explicit left/right slots.
  if (left || right) {
    return (
      <div className={cls}>
        {left ? <div className="v2-toolbar__left">{left}</div> : <div />}
        {right ? <div className="v2-toolbar__right">{right}</div> : null}
      </div>
    );
  }

  // Single-cluster: children wrap as one flexible row.
  return <div className={cls}>{children}</div>;
}
